/**
 * Local Storage Service
 * Gerencia todos os dados persistidos no localStorage
 * Estrutura: tasks, pomodoroConfig, pomodoroSessions, estatísticas
 */

const STORAGE_KEYS = {
  TASKS: 'to-focus:tasks',
  POMODORO_CONFIG: 'to-focus:pomodoroConfig',
  POMODORO_SESSIONS: 'to-focus:pomodoroSessions',
  POMODORO_TIMER: 'to_focus_pomodoro_timer_v1',
}

const DEFAULT_POMODORO_CONFIG = [
  {
    id: 1,
    categoryName: 'PADRÃO',
    focusTime: 25,
    shortBreakTime: 5,
    longBreakTime: 15,
    isVisible: true,
  },
]

/**
 * Inicializa o localStorage com estrutura padrão se vazio
 */
const initializeStorage = () => {
  // Tasks - começa vazio
  if (!localStorage.getItem(STORAGE_KEYS.TASKS)) {
    localStorage.setItem(STORAGE_KEYS.TASKS, JSON.stringify([]))
  }

  // Pomodoro Config - começa com padrão
  if (!localStorage.getItem(STORAGE_KEYS.POMODORO_CONFIG)) {
    localStorage.setItem(
      STORAGE_KEYS.POMODORO_CONFIG,
      JSON.stringify(DEFAULT_POMODORO_CONFIG)
    )
  }

  // Pomodoro Sessions - começa vazio
  if (!localStorage.getItem(STORAGE_KEYS.POMODORO_SESSIONS)) {
    localStorage.setItem(STORAGE_KEYS.POMODORO_SESSIONS, JSON.stringify([]))
  }
}

/**
 * TASKS
 */
const getTasksArray = () => {
  const data = localStorage.getItem(STORAGE_KEYS.TASKS)
  return data ? JSON.parse(data) : []
}

const sortTasksByCompletion = tasks =>
  [...tasks].sort((taskA, taskB) => {
    if (taskA.estaRealizado === taskB.estaRealizado) {
      return 0
    }

    return taskA.estaRealizado ? 1 : -1
  })

const getTasks = () => {
  const tasks = getTasksArray()
  
  // Estrutura as tarefas em matriz de Eisenhower (apenas as visíveis)
  const matrix = {
    URGENTE_IMPORTANTE: [],
    NAO_URGENTE_IMPORTANTE: [],
    URGENTE_NAO_IMPORTANTE: [],
    NAO_URGENTE_NAO_IMPORTANTE: [],
  }
  
  tasks.forEach(task => {
    // Considera visível se não tem o campo ou se visivel === true
    if (task.visivel === false) {
      return
    }
    
    const key = task.prioridade
    if (matrix[key]) {
      matrix[key].push(task)
    }
  })

  Object.keys(matrix).forEach(key => {
    matrix[key] = sortTasksByCompletion(matrix[key])
  })

  return matrix
}

const createTask = (taskData) => {
  const tasks = getTasksArray()
  const newTask = {
    id: Date.now().toString(), // ID único baseado em timestamp
    ...taskData,
    visivel: true, // Nova tarefa sempre visível
    estaRealizado: false, // Padrão: não realizada
    dataCriacao: new Date().toISOString(),
  }
  tasks.push(newTask)
  localStorage.setItem(STORAGE_KEYS.TASKS, JSON.stringify(tasks))
  return newTask
}

const updateTask = (id, taskData) => {
  const tasks = getTasksArray()
  const index = tasks.findIndex((t) => t.id === id)
  if (index === -1) return null

  tasks[index] = { ...tasks[index], ...taskData }
  if (taskData.estaRealizado && !tasks[index].dataCompletada) {
    tasks[index].dataCompletada = new Date().toISOString()
  }
  localStorage.setItem(STORAGE_KEYS.TASKS, JSON.stringify(tasks))
  return tasks[index]
}

// Soft delete: marca como invisível ao invés de remover
const hideTask = (id) => {
  return updateTask(id, { visivel: false })
}

/**
 * POMODORO CONFIG
 */
const getPomodoroConfig = () => {
  const data = localStorage.getItem(STORAGE_KEYS.POMODORO_CONFIG)
  return data ? JSON.parse(data) : DEFAULT_POMODORO_CONFIG
}

const createPomodoroConfig = (configData) => {
  const configs = getPomodoroConfig()
  const newConfig = {
    id: Date.now(),
    ...configData,
    isVisible: true,
  }
  configs.push(newConfig)
  localStorage.setItem(STORAGE_KEYS.POMODORO_CONFIG, JSON.stringify(configs))
  return newConfig
}

const deletePomodoroConfig = (id) => {
  const configs = getPomodoroConfig()
  const filtered = configs.filter((c) => c.id !== id)
  localStorage.setItem(STORAGE_KEYS.POMODORO_CONFIG, JSON.stringify(filtered))
  return true
}

/**
 * POMODORO SESSIONS - Histórico de sessões completadas
 */
const getPomodoroSessions = () => {
  const data = localStorage.getItem(STORAGE_KEYS.POMODORO_SESSIONS)
  return data ? JSON.parse(data) : []
}

const createPomodoroSession = (sessionData) => {
  const sessions = getPomodoroSessions()
  const newSession = {
    id: Date.now().toString(),
    ...sessionData,
    dataInicio: new Date().toISOString(),
  }
  sessions.push(newSession)
  localStorage.setItem(STORAGE_KEYS.POMODORO_SESSIONS, JSON.stringify(sessions))
  return newSession
}

const updatePomodoroSession = (id, sessionData) => {
  const sessions = getPomodoroSessions()
  const index = sessions.findIndex((s) => s.id === id)
  if (index === -1) return null

  sessions[index] = { ...sessions[index], ...sessionData }
  localStorage.setItem(STORAGE_KEYS.POMODORO_SESSIONS, JSON.stringify(sessions))
  return sessions[index]
}

const addPomodoroSessionFocusSeconds = (id, secondsToAdd) => {
  const sessions = getPomodoroSessions()
  const index = sessions.findIndex((s) => s.id === id)
  if (index === -1) return null

  const safeSecondsToAdd = Math.max(0, Math.floor(Number(secondsToAdd) || 0))
  const currentFocusSeconds =
    sessions[index].focusSeconds != null
      ? Math.max(0, Number(sessions[index].focusSeconds) || 0)
      : Math.max(0, Number(sessions[index].focusTime) || 0) * 60

  const nextFocusSeconds = currentFocusSeconds + safeSecondsToAdd

  sessions[index] = {
    ...sessions[index],
    focusSeconds: nextFocusSeconds,
    focusTime: Math.floor(nextFocusSeconds / 60),
  }

  localStorage.setItem(STORAGE_KEYS.POMODORO_SESSIONS, JSON.stringify(sessions))
  return sessions[index]
}

const addPomodoroSessionFocusTime = (id, minutesToAdd) =>
  addPomodoroSessionFocusSeconds(
    id,
    Math.max(0, Number(minutesToAdd) || 0) * 60
  )

const getCurrentFocusSecondsInProgress = () => {
  const rawTimerState = localStorage.getItem(STORAGE_KEYS.POMODORO_TIMER)
  if (!rawTimerState) return 0

  try {
    const timerData = JSON.parse(rawTimerState)
    const activePomodoro = timerData?.activePomodoro
    const timerState = timerData?.timerState
    const focusStartedAt = timerState?.focusStartedAt

    if (
      !activePomodoro ||
      activePomodoro.title !== 'FOCUS' ||
      !activePomodoro.time ||
      timerData.status !== 'PROGRESS' ||
      !focusStartedAt
    ) {
      return 0
    }

    const startedAtMs = new Date(focusStartedAt).getTime()
    if (!Number.isFinite(startedAtMs)) {
      return 0
    }

    const elapsedSeconds = Math.max(
      0,
      Math.floor((Date.now() - startedAtMs) / 1000)
    )

    if (!timerState?.expiryTimestamp) {
      return elapsedSeconds
    }

    const expiryMs = new Date(timerState.expiryTimestamp).getTime()
    if (!Number.isFinite(expiryMs)) {
      return elapsedSeconds
    }

    const segmentSeconds = Math.max(
      0,
      Math.floor((expiryMs - startedAtMs) / 1000)
    )
    return Math.min(elapsedSeconds, segmentSeconds)
  } catch (e) {
    return 0
  }
}

/**
 * ESTATÍSTICAS (calculadas a partir dos dados existentes)
 */
const getStatistics = () => {
  const tasks = getTasksArray()
  const sessions = getPomodoroSessions()

  // Filtra apenas tarefas visíveis para as estatísticas
  const visibleTasks = tasks.filter((t) => t.visivel !== false)

  const tasksCompleted = visibleTasks.filter((t) => t.estaRealizado).length
  const tasksPending = visibleTasks.filter((t) => !t.estaRealizado).length
  const totalTasks = visibleTasks.length

  const pomodorosCompleted = sessions.filter(
    (s) => s.status === 'CONCLUIDO'
  ).length
  const pomodorosIncomplete = sessions.filter(
    (s) => s.status !== 'CONCLUIDO'
  ).length

  const persistedFocusSeconds = sessions.reduce((acc, session) => {
    if (session.focusSeconds != null) {
      return acc + Math.max(0, Number(session.focusSeconds) || 0)
    }

    return acc + Math.max(0, Number(session.focusTime) || 0) * 60
  }, 0)

  // Soma os focos já concluídos com o foco em andamento/pausado da sessão atual
  const totalFocusTime = Math.floor(
    (persistedFocusSeconds + getCurrentFocusSecondsInProgress()) / 60
  )

  return {
    tasksCompleted,
    tasksPending,
    totalTasks,
    pomodorosCompleted,
    pomodorosIncomplete,
    totalFocusTime,
  }
}

/**
 * LIMPEZA (desenvolvimento/debug)
 */
const clearAllStorage = () => {
  localStorage.removeItem(STORAGE_KEYS.TASKS)
  localStorage.removeItem(STORAGE_KEYS.POMODORO_CONFIG)
  localStorage.removeItem(STORAGE_KEYS.POMODORO_SESSIONS)
}

export {
  initializeStorage,
  // Tasks
  getTasks,
  getTasksArray,
  createTask,
  updateTask,
  hideTask,
  // Pomodoro Config
  getPomodoroConfig,
  createPomodoroConfig,
  deletePomodoroConfig,
  // Pomodoro Sessions
  getPomodoroSessions,
  createPomodoroSession,
  updatePomodoroSession,
  addPomodoroSessionFocusTime,
  addPomodoroSessionFocusSeconds,
  // Statistics
  getStatistics,
  // Utils
  clearAllStorage,
}
